if (!customElements.get('collection-color-families')) {
  customElements.define('collection-color-families', class extends HTMLElement {
    constructor() {
      super();
      ['onClick', 'onChange', 'onOver', 'onLeave', 'onFocusOut', 'onKey', 'onOutside', 'sync', 'positionPanel'].forEach(name => { this[name] = this[name].bind(this); });
    }

    connectedCallback() {
      this.scope = this.closest('[data-section-id]');
      const order = ['green', 'blue', 'pink', 'purple', 'red', 'orange', 'yellow', 'beige', 'grey', 'gray', 'brown', 'black'];
      const list = this.querySelector('.collection-color-families__list');
      const wrappers = [...list.querySelectorAll('.collection-color-families__family')];
      if (!this.hasAttribute('data-ordered')) wrappers.sort((a, b) => {
        const rank = wrapper => {
          const index = order.indexOf(wrapper.querySelector('[data-family]').dataset.family);
          return index < 0 ? order.length : index;
        };
        return rank(a) - rank(b);
      }).forEach(wrapper => list.append(wrapper));
      this.hoverButtons = [...this.querySelectorAll('.collection-color-families__button')];
      this.list = list;
      this.mobile = window.matchMedia('(max-width: 899px)');
      const collectionColors = this.collectionColorInputs();
      this.supportedColorValues = new Set(collectionColors.map(input => input.value));
      this.supportedColorLabels = new Set(collectionColors.map(input => this.normalize(input.dataset.label)));
      list.addEventListener('scroll', this.positionPanel, { passive: true });
      window.addEventListener('scroll', this.positionPanel, { passive: true });
      this.resizeObserver = new ResizeObserver(this.positionPanel);
      this.hoverButtons.forEach(button => this.resizeObserver.observe(button));
      this.families = [...this.querySelectorAll('[data-family]')].map(button => ({
        button,
        wrapper: button.parentElement,
        panel: this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`),
        items: JSON.parse(button.parentElement.querySelector('[data-family-items]').textContent).filter(item => this.supportsColor(item)),
      }));
      this.families.forEach(family => {
        family.wrapper.hidden = family.items.length === 0;
        this.append(family.panel);
      });
      const allButton = this.querySelector('[data-family-all]');
      const allPanel = this.families[0].panel.cloneNode(true);
      allPanel.id = `family-all-${this.families[0].panel.id}`;
      allPanel.hidden = true;
      allPanel.classList.add('collection-color-families__panel--all');
      allPanel.querySelector('.collection-color-families__colors').replaceChildren();
      allButton.setAttribute('aria-controls', allPanel.id);
      allButton.setAttribute('aria-expanded', 'false');
      this.append(allPanel);
      const allOnlyItems = JSON.parse(this.querySelector('[data-all-only-items]')?.textContent || '[]').filter(item => this.supportsColor(item));
      this.families.push({ button: allButton, wrapper: allButton, panel: allPanel, isAll: true, items: [...allOnlyItems, ...this.families.flatMap(family => family.items)] });
      this.hidden = this.families.every(family => family.items.length === 0);
      this.events = { click: this.onClick, change: this.onChange, pointerover: this.onOver, pointerleave: this.onLeave, focusout: this.onFocusOut, keydown: this.onKey };
      Object.entries(this.events).forEach(([name, handler]) => this.addEventListener(name, handler));
      document.addEventListener('facets:change', this.sync);
      document.addEventListener('pointerdown', this.onOutside);
      this.sync();
      this.dispatchEvent(new CustomEvent('collection-color-families:ready', { bubbles: true }));
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      this.list.removeEventListener('scroll', this.positionPanel);
      window.removeEventListener('scroll', this.positionPanel);
      Object.entries(this.events).forEach(([name, handler]) => this.removeEventListener(name, handler));
      document.removeEventListener('facets:change', this.sync);
      document.removeEventListener('pointerdown', this.onOutside);
    }

    colorInputs() {
      return [...this.scope.querySelectorAll('#FacetFiltersForm input[type="checkbox"]')].filter(input =>
        ['filter.p.m.shopify.color-pattern', 'filter.v.t.shopify.color-pattern'].includes(input.name)
      );
    }

    normalize(value) {
      return String(value).toLowerCase().replace(/[-_]/g, ' ').trim().replace(/^grey$/, 'gray');
    }

    collectionColorInputs() {
      return this.colorInputs().filter(input => input.hasAttribute('data-label'));
    }

    supportsColor(item) {
      const id = String(item.id);
      const gid = id.startsWith('gid://') ? id : `gid://shopify/Metaobject/${id}`;
      return this.supportedColorValues.has(gid) || this.supportedColorValues.has(id) ||
        (Boolean(item.label) && this.supportedColorLabels.has(this.normalize(item.label)));
    }

    sync() {
      const inputs = this.colorInputs();
      this.paramName = inputs[0]?.name || 'filter.v.t.shopify.color-pattern';
      const params = new URLSearchParams(window.location.search);
      this.selected = new Set(params.getAll(this.paramName).flatMap(value => value.split(',')));
      this.families.forEach(family => {
        if (!family.colors) {
          const colors = new Map();
          family.items.forEach(item => {
            const key = this.normalize(item.label);
            if (!item.label) return;
            if (!colors.has(key)) colors.set(key, { label: item.label, color: item.color, image: item.image, ids: [], isNew: false });
            if (item.new) colors.get(key).isNew = true;
            colors.get(key).ids.push(`gid://shopify/Metaobject/${item.id}`);
          });
          family.colors = [...colors.values()];
          if (family.isAll) family.colors = customElements.get('collection-color-order').sort(family.colors, this.scope);
          const grid = family.panel.querySelector('.collection-color-families__colors');
          family.colors.forEach(color => {
            const label = document.createElement('label');
            label.className = 'collection-color-families__color';
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.dataset.colorLabel = color.label;
            const swatch = document.createElement('span');
            swatch.className = 'collection-color-families__color-swatch';
            swatch.setAttribute('aria-hidden', 'true');
            swatch.style.backgroundColor = color.color || '';
            if (color.image) swatch.style.backgroundImage = `url("${color.image}")`;
            if (this.normalize(color.label) === 'white') {
              swatch.style.setProperty('--swatch-check-color', '#111');
            }
            swatch.append(this.querySelector('[data-color-check]').content.cloneNode(true));
            if (color.isNew) swatch.append(this.querySelector('[data-color-new-label]').content.cloneNode(true));
            const text = document.createElement('span');
            text.textContent = color.label;
            label.append(checkbox, swatch, text);
            grid.append(label);
            color.checkbox = checkbox;
          });
        }
        family.colors.forEach(color => {
          const matches = inputs.filter(input => color.ids.includes(input.value) ||
            this.normalize(input.dataset.label) === this.normalize(color.label));
          if (matches.length) color.values = matches.map(input => input.value);
          else if (!color.values) color.values = color.ids;
        });
      });
      this.updateState();
      this.writeSelections();
    }

    updateState() {
      const selectedColors = new Set();
      this.families.forEach(family => {
        family.colors.forEach(color => {
          color.checkbox.checked = color.values.every(value => this.selected.has(value));
        });
        const selected = family.colors.filter(color => color.checkbox.checked).length;
        family.colors.filter(color => color.checkbox.checked).forEach(color => selectedColors.add(this.normalize(color.label)));
        const name = family.button.querySelector('span:last-child');
        family.button.dataset.familyLabel ||= name.textContent;
        name.textContent = selected ? `${family.button.dataset.familyLabel} (${selected})` : family.button.dataset.familyLabel;
        const all = family.panel.querySelector('[data-select-family]');
        all.checked = family.colors.length > 0 && selected === family.colors.length;
        all.indeterminate = selected > 0 && selected < family.colors.length;
        family.button.setAttribute('aria-pressed', String(selected > 0));
      });
      const allButton = this.querySelector('[data-family-all]');
      const allName = allButton.querySelector('span:last-child');
      allButton.dataset.familyLabel ||= allName.textContent;
      allName.textContent = selectedColors.size ? `${allButton.dataset.familyLabel} (${selectedColors.size})` : allButton.dataset.familyLabel;
      allButton.setAttribute('aria-pressed', String(selectedColors.size > 0));
    }

    open(family) {
      this.families.forEach(item => {
        item.panel.hidden = item !== family;
        item.button.setAttribute('aria-expanded', String(item === family));
      });
      this.openFamily = family;
      this.classList.toggle('has-open-family', Boolean(family));
      if (family && this.mobile.matches) {
        const rect = family.button.getBoundingClientRect();
        const listRect = this.list.getBoundingClientRect();
        const centered = this.list.scrollLeft + rect.left + rect.width / 2 - listRect.left - listRect.width / 2;
        this.list.scrollTo({
          left: Math.max(0, Math.min(centered, this.list.scrollWidth - this.list.clientWidth)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
      }
      this.positionPanel();
    }

    positionPanel() {
      const family = this.openFamily;
      if (family) {
        const rect = family.button.getBoundingClientRect();
        const root = this.getBoundingClientRect();
        this.style.setProperty('--family-overlay-top', `${Math.max(0, root.bottom)}px`);
        const width = family.panel.getBoundingClientRect().width;
        const left = this.mobile.matches ? 15 : Math.max(8, Math.min(rect.left - root.left + rect.width / 2 - width / 2, root.width - width - 8));
        family.panel.style.left = `${left}px`;
        family.panel.style.setProperty('--pointer-left', `${rect.left - root.left + rect.width / 2 - left}px`);
      }
    }

    writeSelections() {
      const form = this.scope.querySelector('#FacetFiltersForm');
      if (!form) return;
      form.querySelector('[data-family-selections]')?.remove();
      const inputs = this.colorInputs();
      inputs.forEach(input => {
        input.checked = this.selected.has(input.value);
        if (input.checked) input.disabled = false;
      });
      const extra = document.createElement('div');
      extra.hidden = true;
      extra.dataset.familySelections = '';
      this.selected.forEach(value => {
        if (inputs.some(input => input.value === value)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = this.paramName;
        input.value = value;
        extra.append(input);
      });
      form.append(extra);
    }

    submit() {
      this.writeSelections();
      this.updateState();
      const form = this.scope.querySelector('#FacetFiltersForm');
      if (this.mobile.matches && this.scope.querySelector('collection-inline-filters')) {
        this.scope.inlineFilterReturnToHeading = true;
      }
      form?.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onClick(event) {
      if (this.mobile.matches && event.target === this) {
        this.open(null);
        return;
      }
      const button = event.target.closest('button');
      if (button?.hasAttribute('data-family-all')) {
        const allFamily = this.families.find(item => item.button === button);
        if (this.mobile.matches && this.openFamily === allFamily) {
          this.open(null);
          return;
        }
        this.selected.clear();
        this.open(allFamily);
        this.submit();
      } else if (button?.hasAttribute('data-family')) {
        const family = this.families.find(item => item.button === button);
        if (this.mobile.matches) {
          this.open(this.openFamily === family ? null : family);
          return;
        }
        const deselect = family.colors.length > 0 && family.colors.every(color =>
          color.values.every(value => this.selected.has(value))
        );
        family.colors.forEach(color => color.values.forEach(value => {
          if (deselect) this.selected.delete(value);
          else this.selected.add(value);
        }));
        this.open(family);
        this.submit();
      }
    }

    onChange(event) {
      const family = this.families.find(item => item.panel.contains(event.target));
      if (!family) return;
      const colors = event.target.hasAttribute('data-select-family') ? family.colors :
        family.colors.filter(color => color.checkbox === event.target);
      colors.forEach(color => color.values.forEach(value => {
        if (event.target.checked) this.selected.add(value);
        else this.selected.delete(value);
      }));
      if (this.mobile.matches) this.open(null);
      this.submit();
    }

    onOver(event) {
      if (this.mobile.matches || event.pointerType !== 'mouse') return;
      const button = event.target.closest('.collection-color-families__button');
      if (button) this.setHover(button);
      const family = this.families.find(item => item.button.contains(event.target));
      if (family) this.open(family);
      else if (event.target !== this && !event.target.closest('.collection-color-families__panel')) {
        this.setHover(null);
        this.open(null);
      }
    }
    setHover(button) {
      const index = this.hoverButtons.indexOf(button);
      this.hoverButtons.forEach((item, i) => {
        const distance = index < 0 ? Infinity : Math.abs(i - index);
        item.classList.toggle('is-hovered', distance === 0);
        item.classList.toggle('is-nearby-1', distance === 1);
        item.classList.toggle('is-nearby-2', distance === 2);
      });
    }
    onLeave() {
      if (this.mobile.matches) return;
      this.setHover(null);
      this.open(null);
    }
    onFocusOut(event) { if (!this.syncing && !this.contains(event.relatedTarget)) this.open(null); }
    onOutside(event) {
      if (!this.contains(event.target)) {
        this.setHover(null);
        this.open(null);
      }
    }
    onKey(event) {
      if (event.key === 'Escape') {
        const family = this.families.find(item => !item.panel.hidden);
        this.open(null);
        family?.button.focus();
      }
      if (event.key === 'ArrowDown' && event.target.hasAttribute('data-family')) {
        event.preventDefault();
        const family = this.families.find(item => item.button === event.target);
        this.open(family);
        family.panel.querySelector('input:not(:disabled)')?.focus();
      }
    }
  });
}

